import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { BarChart, Bar, Cell, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts'
import { db } from '../../firebase/config'
import { client } from '../../hooks/Client'
import { useFirestoreOrderBy } from '../../firebase/useFirestore'
import averageNumericInputs from '../../helpers/analysisHelpers/averageNumericInputs'
import { buildRowLookup } from '../../helpers/analysisHelpers/matrixRowLookup'
import DeepAnalysisSectionHeader from './DeepAnalysisSectionHeader'
import notDeleted from '../../helpers/notDeleted'
import isQuestionField from '../../helpers/questionnaire/isQuestionField'
import { CHART_MARK_STROKE, CHART_MARK_STROKE_WIDTH } from '../../helpers/chartPalette'
import useChartColors from '../../hooks/useChartColors'
import DeepAnalysisPeriodContext from '../../StateManagment/DeepAnalysisPeriodContext'
import ComparisonFrame from '../dataVisualizations/ComparisonFrame'
import filterSubmissionsByPeriod from '../../helpers/compareHelpers/filterSubmissionsByPeriod'
import sharedAxisMax, { axisTicks } from '../../helpers/compareHelpers/sharedAxisMax'

/**
 * @typedef {object} FieldScale
 * @property {number} lowerScale - Field scale lower bound (ReachStart)
 * @property {number} upperScale - Field scale upper bound (ReachEnd)
 *
 * @typedef {object} CommonQuestion
 * @property {string} question - The shared question text
 * @property {string} type - Field type (scale, matrix-one, ...)
 * @property {string} idA - FieldID of this question in the anchor research's questionnaire
 * @property {string} idB - FieldID in the comparison research's questionnaire
 * @property {FieldScale} scaleA - Scale of the anchor field (for mirroring negative rows)
 * @property {FieldScale} scaleB - Scale of the comparison field
 */

// Legacy docs can carry string scale bounds — coerce before the mirror
// arithmetic (same defaults as the server-side scale builders).
const toScale = (value, fallback) => {
    const num = Number(value)
    return value != null && value !== '' && Number.isFinite(num) ? num : fallback
}

/**
 * @typedef {object} CompareRaw
 * @property {object[]} recordsA - Anchor research answers to the chosen question
 * @property {object[]} recordsB - Comparison research answers
 * @property {object} rowLookupA - Matrix row lookup of the anchor field
 * @property {object} rowLookupB - Matrix row lookup of the comparison field
 */

/**
 * One period's comparison: the two averages as bars plus their counts.
 *
 * @param {object} props
 * @param {{a: {mean: number|null, n: number}, b: {mean: number|null, n: number}}} props.results - Averages for this period
 * @param {string} props.titleA - Anchor research title
 * @param {string} props.titleB - Comparison research title
 * @param {boolean} props.hasPeriod - These averages cover a chosen period (changes the empty wording)
 * @param {[string, string]} props.colors - Bar colours (anchor, comparison)
 * @param {number} [props.yMax] - Top of the value axis, shared by both compared periods
 */
const ComparePanel = ({ results, titleA, titleB, hasPeriod, colors, yMax }) => {
    const [kleurDitOnderwerp, kleurVergelijking] = colors
    if (results.a.mean === null || results.b.mean === null) {
        const missing = results.a.mean === null ? titleA : titleB
        return (
            <p className='deep-analysis-empty'>
                {hasPeriod
                    ? `In deze periode heeft "${missing}" geen numerieke antwoorden op deze vraag.`
                    : `Deze vraag heeft in "${missing}" geen numerieke antwoorden om te vergelijken.`}
            </p>
        )
    }
    const chartData = [
        { name: titleA, score: Math.round(results.a.mean * 100) / 100 },
        { name: titleB, score: Math.round(results.b.mean * 100) / 100 },
    ]
    return (
        <>
            <div className='deep-analysis-compare-chart'>
                <ResponsiveContainer width='100%' height={220}>
                    <BarChart data={chartData} margin={{ top: 10, right: 20, bottom: 10, left: 0 }}>
                        <CartesianGrid strokeDasharray='3 3' />
                        <XAxis dataKey='name' />
                        <YAxis domain={yMax ? [0, yMax] : undefined} ticks={axisTicks(yMax)} allowDecimals={false} />
                        <Tooltip />
                        <Bar dataKey='score' name='Gemiddelde score' stroke={CHART_MARK_STROKE} strokeWidth={CHART_MARK_STROKE_WIDTH}>
                            <Cell fill={kleurDitOnderwerp} stroke={CHART_MARK_STROKE} strokeWidth={CHART_MARK_STROKE_WIDTH} />
                            <Cell fill={kleurVergelijking} stroke={CHART_MARK_STROKE} strokeWidth={CHART_MARK_STROKE_WIDTH} />
                        </Bar>
                    </BarChart>
                </ResponsiveContainer>
            </div>
            <p className='deep-analysis-scatter-count'>
                {titleA}: gemiddeld {chartData[0].score} ({results.a.n} antwoorden) ·{' '}
                {titleB}: gemiddeld {chartData[1].score} ({results.b.n} antwoorden)
            </p>
        </>
    )
}

/**
 * "+ Vergelijk met…" section on a research deep-analysis page.
 *
 * The page's anchor research is fixed; the user picks a second research.
 * Questions shared by both questionnaires (matched on text + type, same
 * heuristic as the old CompareResearch builder) can then be compared:
 * average score per research, side by side.
 *
 * Follows the page period (DeepAnalysisPeriodContext): the answers are read
 * once, then averaged per period over the whole submissions inside it
 * (filterSubmissionsByPeriod) — one chart per period when comparing.
 * AI-CONTEXT: See docs/features/deep-analysis-period.md
 *
 * @param {object} props
 * @param {object} props.anchor - The anchor Research doc
 */
const CompareSection = ({ anchor }) => {
    const { palette } = useChartColors()
    // De twee vergeleken onderwerpen. Namen bewust niet 'PrimaryColor':
    // dit is een paletpositie, geen huisstijlkleur.
    const [kleurDitOnderwerp, kleurVergelijking] = palette
    const period = useContext(DeepAnalysisPeriodContext)
    const compare = Boolean(period?.compareActive)

    const [compareId, setCompareId] = useState('')
    const [commonQuestions, setCommonQuestions] = useState(/** @type {CommonQuestion[] | null} */ (null))
    const [selectedQuestion, setSelectedQuestion] = useState(/** @type {CommonQuestion | null} */ (null))
    const [raw, setRaw] = useState(/** @type {CompareRaw | null} */ (null))
    const [error, setError] = useState('')

    const allResearch = useFirestoreOrderBy('Research', 'Position', 'asc')
    const otherResearch = (Array.isArray(allResearch) ? allResearch : [])
        .filter((r) => r.ID !== anchor.ID && !r.Deleted)
    const compareResearch = otherResearch.find((r) => r.ID === compareId)

    // Each choice gets a number; answers that come back for an older choice
    // are dropped, or a slow first question overwrites the one chosen after it.
    const requestRef = useRef(0)

    // Find questions both questionnaires share, keyed by text + type.
    useEffect(() => {
        setCommonQuestions(null)
        setSelectedQuestion(null)
        requestRef.current += 1
        setRaw(null)
        setError('')
        if (!compareResearch) return

        let cancelled = false
        const load = async () => {
            const fetchFields = async (questionnaireId) => {
                const snapshot = await db.collection('QuestionnaireFields')
                    .where('CompagnyID', '==', client)
                    .where('QuestionnaireID', '==', questionnaireId)
                    .get()
                return snapshot.docs
                    .map((doc) => doc.data())
                    .filter((field) => notDeleted(field) && isQuestionField(field))
            }

            const [fieldsA, fieldsB] = await Promise.all([
                fetchFields(anchor.QuestionnaireID),
                fetchFields(compareResearch.QuestionnaireID),
            ])

            const common = fieldsA
                .map((a) => {
                    const match = fieldsB.find((b) => b.Question === a.Question && b.Type === a.Type)
                    if (!match) return null
                    return {
                        question: a.Question,
                        type: a.Type,
                        idA: a.ID,
                        idB: match.ID,
                        scaleA: { lowerScale: toScale(a.ReachStart, 0), upperScale: toScale(a.ReachEnd, 5) },
                        scaleB: { lowerScale: toScale(match.ReachStart, 0), upperScale: toScale(match.ReachEnd, 5) },
                    }
                })
                .filter(Boolean)

            if (!cancelled) setCommonQuestions(common)
        }

        load().catch((err) => {
            console.error('CompareSection — common questions failed:', err)
            if (!cancelled) setError('Vergelijkbare vragen konden niet worden geladen.')
        })

        return () => { cancelled = true }
    }, [compareResearch, anchor.QuestionnaireID, anchor.ID])

    // Fetch both response sets for the chosen question; the averaging per
    // period happens below, so changing the period needs no new read.
    const selectQuestion = (question) => {
        const request = ++requestRef.current
        setSelectedQuestion(question)
        setRaw(null)
        setError('')
        if (!question) return

        const fetchResponses = async (fieldId, researchId) => {
            const snapshot = await db.collection('QuestionnairesResponses')
                .where('CompagnyID', '==', client)
                .where('FieldID', '==', fieldId)
                .where('ResearchID', '==', researchId)
                .get()
            const records = []
            snapshot.docs.filter((doc) => notDeleted(doc.data())).forEach((doc) => records.push(doc.data()))
            return records
        }

        // Rows indexed by RowID and RowTitle: an answer given before the row was
        // reworded carries the old title, and matching on title alone left it
        // unmirrored — a negative item then averaged as if it were positive.
        const fetchRowLookup = async (fieldId) => {
            const snapshot = await db.collection('MatrixQuestionRows')
                .where('Field', '==', fieldId)
                .get()
            return buildRowLookup(
                snapshot.docs.map((doc) => doc.data()).filter(notDeleted)
            )
        }

        const isMatrix = question.type === 'matrix-one' || question.type === 'matrix-multiple'
        const emptyLookup = buildRowLookup([])

        Promise.all([
            fetchResponses(question.idA, anchor.ID),
            fetchResponses(question.idB, compareId),
            isMatrix ? fetchRowLookup(question.idA) : Promise.resolve(emptyLookup),
            isMatrix ? fetchRowLookup(question.idB) : Promise.resolve(emptyLookup),
        ])
            .then(([recordsA, recordsB, rowLookupA, rowLookupB]) => {
                if (request === requestRef.current) setRaw({ recordsA, recordsB, rowLookupA, rowLookupB })
            })
            .catch((err) => {
                console.error('CompareSection — responses failed:', err)
                if (request === requestRef.current) setError('De antwoorden konden niet worden geladen.')
            })
    }

    // Matrix rows can be negatively worded — mirror them within each field's
    // own scale, keyed per field (never a shared title map).
    // AI-CONTEXT: See docs/pitfalls.md#database-raw-input-skips-negative-mirroring
    const resultsFor = useCallback((range) => {
        if (!raw || !selectedQuestion) return null
        return {
            a: averageNumericInputs(filterSubmissionsByPeriod(raw.recordsA, range), { rowLookup: raw.rowLookupA, ...selectedQuestion.scaleA }),
            b: averageNumericInputs(filterSubmissionsByPeriod(raw.recordsB, range), { rowLookup: raw.rowLookupB, ...selectedQuestion.scaleB }),
        }
    }, [raw, selectedQuestion])

    // One axis across both periods, or equally tall bars mean different
    // numbers (testronde 2026-10-28, B2). AI-CONTEXT: See docs/features/deep-analysis-period.md
    const rangeA = period?.rangeA || null
    const rangeB = period?.rangeB || null
    const resultsBySide = useMemo(() => ({
        A: resultsFor(rangeA),
        B: compare ? resultsFor(rangeB) : null,
    }), [resultsFor, rangeA, rangeB, compare])

    const yMax = compare && resultsBySide.A && resultsBySide.B
        ? sharedAxisMax([resultsBySide.A, resultsBySide.B].flatMap((r) => [r.a.mean, r.b.mean]))
        : undefined

    const panel = (range, side) => (
        <ComparePanel
            yMax={yMax}
            results={resultsBySide[side === 'B' ? 'B' : 'A']}
            titleA={anchor.Title}
            titleB={compareResearch?.Title || ''}
            hasPeriod={Boolean(range)}
            colors={[kleurDitOnderwerp, kleurVergelijking]}
        />
    )

    return (
        <div className='deep-analysis-section'>
            <DeepAnalysisSectionHeader
                kicker='Vergelijken'
                title='Vergelijk met een ander onderzoek'
                description={`Vergelijk scores op gedeelde vragen tussen "${anchor.Title}" en een tweede onderzoek.`}
            />
            <select
                className='deep-analysis-indicator-select'
                value={compareId}
                onChange={(e) => setCompareId(e.target.value)}
            >
                <option value=''>-- Selecteer onderzoek --</option>
                {otherResearch.map((r) => (
                    <option key={r.ID} value={r.ID}>{r.Title}</option>
                ))}
            </select>
            {error && <p className='deep-analysis-error'>{error}</p>}
            {compareResearch && commonQuestions && commonQuestions.length === 0 && (
                <p className='deep-analysis-empty'>
                    Deze onderzoeken delen geen vragen (zelfde vraagtekst en vraagtype),
                    dus er valt niets te vergelijken.
                </p>
            )}
            {compareResearch && commonQuestions && commonQuestions.length > 0 && (
                <select
                    className='deep-analysis-indicator-select'
                    value={selectedQuestion ? selectedQuestion.idA : ''}
                    onChange={(e) => selectQuestion(commonQuestions.find((q) => q.idA === e.target.value) || null)}
                >
                    <option value=''>-- Selecteer vraag --</option>
                    {commonQuestions.map((q) => (
                        <option key={q.idA} value={q.idA}>{q.question}</option>
                    ))}
                </select>
            )}
            {selectedQuestion && raw && (compare
                ? <ComparisonFrame rangeA={period.rangeA} rangeB={period.rangeB} renderViz={panel} />
                : panel(period?.rangeA || null))}
        </div>
    )
}

export default CompareSection
